import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Bell, BellOff, BellRing, Smartphone, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

const getAuthHeaders = () => ({ 
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } 
});

// Translations for push notification settings
const pushTranslations = {
  lt: {
    title: 'Push pranešimai',
    subtitle: 'Gaukite pranešimus apie priskirtus reisus',
    subscribedText: 'Jūs gaunate pranešimus apie naujus reisus',
    notSupported: 'Push pranešimai nepalaikomi',
    notSupportedHint: 'Naudokite Chrome, Edge arba Firefox Android/Windows',
    blocked: 'Užblokuota',
    subscription: 'Prenumerata',
    permission: 'Leidimas',
    thisDevice: 'Šis įrenginys',
    enable: 'Įjungti',
    disable: 'Išjungti',
    enabled: 'Pranešimai įjungti!',
    disabled: 'Pranešimai išjungti',
    permissionDenied: 'Pranešimų leidimas atmestas',
    enableError: 'Nepavyko įjungti pranešimų',
    disableError: 'Klaida išjungiant pranešimus',
    keyNotConfigured: 'Pranešimų raktas nesukonfigūruotas'
  },
  ru: {
    title: 'Push-уведомления',
    subtitle: 'Получайте уведомления о назначенных рейсах',
    subscribedText: 'Вы получаете уведомления о новых рейсах',
    notSupported: 'Push-уведомления не поддерживаются',
    notSupportedHint: 'Используйте Chrome, Edge или Firefox на Android/Windows',
    blocked: 'Заблокировано',
    subscription: 'Подписка',
    permission: 'Разрешение',
    thisDevice: 'Это устройство',
    enable: 'Включить',
    disable: 'Отключить',
    enabled: 'Уведомления включены!',
    disabled: 'Уведомления отключены',
    permissionDenied: 'Разрешение на уведомления отклонено',
    enableError: 'Не удалось включить уведомления',
    disableError: 'Ошибка отключения уведомлений',
    keyNotConfigured: 'Ключ для уведомлений не настроен'
  },
  en: {
    title: 'Push Notifications',
    subtitle: 'Receive notifications about assigned trips',
    subscribedText: 'You are receiving notifications about new trips',
    notSupported: 'Push notifications are not supported',
    notSupportedHint: 'Use Chrome, Edge or Firefox on Android/Windows',
    blocked: 'Blocked',
    subscription: 'Subscription',
    permission: 'Permission',
    thisDevice: 'This device',
    enable: 'Enable',
    disable: 'Disable',
    enabled: 'Notifications enabled!',
    disabled: 'Notifications disabled',
    permissionDenied: 'Notification permission denied',
    enableError: 'Failed to enable notifications',
    disableError: 'Error disabling notifications',
    keyNotConfigured: 'Notification key not configured'
  },
  pl: {
    title: 'Powiadomienia push',
    subtitle: 'Otrzymuj powiadomienia o przypisanych kursach',
    subscribedText: 'Otrzymujesz powiadomienia o nowych kursach',
    notSupported: 'Powiadomienia push nie są obsługiwane',
    notSupportedHint: 'Użyj Chrome, Edge lub Firefox na Android/Windows',
    blocked: 'Zablokowane',
    subscription: 'Subskrypcja',
    permission: 'Uprawnienia',
    thisDevice: 'To urządzenie',
    enable: 'Włącz',
    disable: 'Wyłącz',
    enabled: 'Powiadomienia włączone!',
    disabled: 'Powiadomienia wyłączone',
    permissionDenied: 'Uprawnienia do powiadomień odrzucone',
    enableError: 'Nie udało się włączyć powiadomień',
    disableError: 'Błąd wyłączania powiadomień',
    keyNotConfigured: 'Klucz powiadomień nie skonfigurowany'
  }
};

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function PushNotificationSettings({ language = 'ru' }) {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);

  // Get translations for current language
  const t = pushTranslations[language] || pushTranslations.ru;

  useEffect(() => {
    checkSupport();
  }, []);

  const checkSupport = async () => {
    try {
      // Check if push notifications are supported
      const supported = 'serviceWorker' in navigator && 
                       'PushManager' in window && 
                       'Notification' in window;
      
      setIsSupported(supported);
      
      if (!supported) {
        setLoading(false);
        return;
      }

      // Check notification permission
      setPermission(Notification.permission);

      // Check if already subscribed
      const statusRes = await axios.get(`${API}/api/push/status`, getAuthHeaders());
      setIsSubscribed(statusRes.data.subscribed);
      
    } catch (error) {
      console.error('Error checking push status:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribe = async () => {
    setSubscribing(true);
    
    try {
      // Request notification permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm !== 'granted') {
        toast.error(t.permissionDenied);
        setSubscribing(false);
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Get VAPID public key
      let vapidKey = VAPID_PUBLIC_KEY;
      if (!vapidKey) {
        const keyRes = await axios.get(`${API}/api/push/vapid-key`);
        vapidKey = keyRes.data.publicKey;
      }

      if (!vapidKey) {
        toast.error(t.keyNotConfigured);
        setSubscribing(false);
        return;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      // Send subscription to server
      const subscriptionJson = subscription.toJSON();
      await axios.post(`${API}/api/push/subscribe`, {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      }, getAuthHeaders());

      setIsSubscribed(true);
      toast.success(t.enabled);
      
    } catch (error) {
      console.error('Subscribe error:', error);
      toast.error(t.enableError);
    } finally {
      setSubscribing(false);
    }
  };

  const unsubscribe = async () => {
    setSubscribing(true);
    
    try {
      // Unsubscribe from push manager
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Remove from server
      await axios.delete(`${API}/api/push/unsubscribe`, getAuthHeaders());
      
      setIsSubscribed(false);
      toast.success(t.disabled);
      
    } catch (error) {
      console.error('Unsubscribe error:', error);
      toast.error(t.disableError);
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-4" data-testid="push-settings-loading">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </Card>
    );
  }

  if (!isSupported) {
    return (
      <Card className="p-4 bg-gray-50 border-gray-200" data-testid="push-settings-unsupported">
        <div className="flex items-center gap-3 text-gray-500">
          <BellOff className="w-5 h-5" />
          <div>
            <p className="font-medium">{t.notSupported}</p>
            <p className="text-sm text-gray-400">{t.notSupportedHint}</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4" data-testid="push-settings-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isSubscribed ? 'bg-green-100' : 'bg-gray-100'
          }`}>
            {isSubscribed ? (
              <BellRing className="w-5 h-5 text-green-600" />
            ) : (
              <Bell className="w-5 h-5 text-gray-500" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">{t.title}</h3>
            <p className="text-sm text-gray-500">
              {isSubscribed ? t.subscribedText : t.subtitle}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {permission === 'denied' ? (
            <div className="flex items-center gap-1.5 text-red-500 text-sm">
              <XCircle className="w-4 h-4" />
              <span>{t.blocked}</span>
            </div>
          ) : isSubscribed ? (
            <Button
              variant="outline"
              size="sm"
              onClick={unsubscribe}
              disabled={subscribing}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="push-disable-btn"
            >
              {subscribing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <BellOff className="w-4 h-4 mr-1" />
                  {t.disable}
                </>
              )}
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={subscribe}
              disabled={subscribing}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="push-enable-btn"
            >
              {subscribing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-1" />
                  {t.enable}
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Status indicators */}
      <div className="mt-4 pt-4 border-t flex items-center gap-4 text-sm">
        <div className={`flex items-center gap-1.5 ${isSubscribed ? 'text-green-600' : 'text-gray-400'}`} data-testid="push-subscription-status">
          {isSubscribed ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          <span>{t.subscription}</span>
        </div>
        <div className={`flex items-center gap-1.5 ${permission === 'granted' ? 'text-green-600' : 'text-gray-400'}`} data-testid="push-permission-status">
          {permission === 'granted' ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          <span>{t.permission}</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-400">
          <Smartphone className="w-4 h-4" />
          <span>{t.thisDevice}</span>
        </div>
      </div>
    </Card>
  );
}
